import { createFileRoute } from "@tanstack/react-router";
import { useState, useRef, useEffect } from "react";
import { z } from "zod";
import {
  Container,
  Paper,
  Title,
  Text,
  TextInput,
  Textarea,
  Select,
  Button,
  Stack,
  Group,
  Alert,
  FileButton,
  Image,
  Center,
  Loader,
  ThemeIcon,
  Box,
  Badge,
} from "@mantine/core";
import {
  IconCheck,
  IconAlertCircle,
  IconCamera,
  IconX,
  IconSend,
  IconWifi,
  IconWifiOff,
  IconClock,
} from "@tabler/icons-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import {
  saveSubmissionOffline,
  cacheProjectInfo,
  getCachedProjectInfo,
  initInboxSync,
  cleanupInboxSync,
  subscribeInboxSyncStatus,
  triggerInboxSync,
  getPendingSubmissionsByToken,
  isIndexedDBAvailable,
  type InboxSyncStatus,
  type OfflineInboxSubmission,
} from "@/lib/inbox-offline";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8787";

const searchSchema = z.object({
  name: z.string().optional(),
  ref: z.string().optional(),
});

export const Route = createFileRoute("/submit/$token")({
  component: SubmitPage,
  validateSearch: searchSchema,
});

interface ProjectInfo {
  projectName: string;
  isActive: boolean;
}

interface SubmitResponse {
  success: boolean;
  referenceNumber: string;
  message: string;
}

function SubmitPage() {
  const { token } = Route.useParams();
  const { name: prefillName, ref } = Route.useSearch();
  const { isOnline } = useNetworkStatus();

  const [submitterName, setSubmitterName] = useState(prefillName || "");
  const [submitterPhone, setSubmitterPhone] = useState("");
  const [submitterEmail, setSubmitterEmail] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState<string | null>("general");
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const resetRef = useRef<() => void>(null);

  // Offline state
  const [syncStatus, setSyncStatus] = useState<InboxSyncStatus>({
    isSyncing: false,
    pendingCount: 0,
  });
  const [queuedSubmission, setQueuedSubmission] = useState<OfflineInboxSubmission | null>(null);
  const [cachedProject, setCachedProject] = useState<{ projectName: string } | null>(null);
  const [pendingSubmissions, setPendingSubmissions] = useState<OfflineInboxSubmission[]>([]);

  // Update name if prefillName changes (e.g., on initial load)
  useEffect(() => {
    if (prefillName && !submitterName) {
      setSubmitterName(prefillName);
    }
  }, [prefillName]);

  // Initialize offline sync
  useEffect(() => {
    if (!isIndexedDBAvailable()) return;

    initInboxSync();

    const unsubscribe = subscribeInboxSyncStatus((status) => {
      setSyncStatus(status);
      // Reload pending submissions when sync status changes
      loadPendingSubmissions();
    });

    // Load cached project and pending submissions
    loadCachedProject();
    loadPendingSubmissions();

    return () => {
      unsubscribe();
      cleanupInboxSync();
    };
  }, [token]);

  // Trigger sync when coming back online
  useEffect(() => {
    if (isOnline && syncStatus.pendingCount > 0) {
      triggerInboxSync();
    }
  }, [isOnline]);

  async function loadCachedProject() {
    const cached = await getCachedProjectInfo(token);
    if (cached) {
      setCachedProject(cached);
    }
  }

  async function loadPendingSubmissions() {
    const pending = await getPendingSubmissionsByToken(token);
    setPendingSubmissions(pending.filter(s => s.syncStatus !== "synced"));
  }

  // Fetch project info
  const {
    data: projectInfo,
    isLoading: loadingProject,
    error: projectError,
  } = useQuery<ProjectInfo>({
    queryKey: ["inbox", token],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/inbox/${token}`);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Invalid or inactive link");
      }
      const data = await response.json();

      // Cache project info for offline use
      if (isIndexedDBAvailable()) {
        await cacheProjectInfo(token, data.projectName);
        setCachedProject({ projectName: data.projectName });
      }

      return data;
    },
    retry: isOnline ? 3 : 0, // Don't retry when offline
    enabled: isOnline, // Only fetch when online
  });

  // Submit mutation (for online submissions)
  const submitMutation = useMutation<SubmitResponse, Error>({
    mutationFn: async () => {
      const endpoint = photo
        ? `${API_URL}/api/inbox/${token}/submit-with-photo`
        : `${API_URL}/api/inbox/${token}/submit`;

      if (photo) {
        const formData = new FormData();
        formData.append("submitterName", submitterName);
        if (submitterPhone) formData.append("submitterPhone", submitterPhone);
        if (submitterEmail) formData.append("submitterEmail", submitterEmail);
        formData.append("message", message);
        formData.append("category", category || "general");
        formData.append("photo", photo);
        if (ref) formData.append("ref", ref);

        const response = await fetch(endpoint, {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || "Failed to submit");
        }
        return response.json();
      } else {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            submitterName,
            submitterPhone: submitterPhone || undefined,
            submitterEmail: submitterEmail || undefined,
            message,
            category: category || "general",
            ref: ref || undefined,
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || "Failed to submit");
        }
        return response.json();
      }
    },
  });

  // Handle photo selection
  const handlePhotoSelect = (file: File | null) => {
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Clear photo
  const clearPhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    resetRef.current?.();
  };

  // Handle submit (online or offline)
  const handleSubmit = async () => {
    if (!submitterName.trim() || !message.trim()) {
      return;
    }

    if (isOnline) {
      // Try online submission
      submitMutation.mutate();
    } else {
      // Queue for offline sync
      await queueOfflineSubmission();
    }
  };

  // Queue submission for later sync
  const queueOfflineSubmission = async () => {
    if (!isIndexedDBAvailable()) {
      return;
    }

    try {
      // Convert photo to blob if present
      let photoBlob: Blob | undefined;
      let photoFilename: string | undefined;

      if (photo) {
        photoBlob = photo;
        photoFilename = photo.name;
      }

      const submission = await saveSubmissionOffline({
        token,
        submitterName,
        submitterPhone: submitterPhone || undefined,
        submitterEmail: submitterEmail || undefined,
        message,
        category: category || "general",
        ref: ref || undefined,
        photoBlob,
        photoFilename,
        projectName: cachedProject?.projectName || projectInfo?.projectName,
      });

      setQueuedSubmission(submission);
      await loadPendingSubmissions();
    } catch (error) {
      console.error("Failed to queue submission:", error);
    }
  };

  // Reset form
  const resetForm = () => {
    setSubmitterName(prefillName || "");
    setSubmitterPhone("");
    setSubmitterEmail("");
    setMessage("");
    setCategory("general");
    clearPhoto();
    submitMutation.reset();
    setQueuedSubmission(null);
  };

  // Get display project name (from API or cache)
  const displayProjectName = projectInfo?.projectName || cachedProject?.projectName;

  // Loading state (only when online and loading)
  if (isOnline && loadingProject && !cachedProject) {
    return (
      <Container size="sm" py="xl">
        <Center style={{ minHeight: "50vh" }}>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  // Error state - invalid link (only show if online and no cache)
  if (isOnline && projectError && !cachedProject) {
    return (
      <Container size="sm" py="xl">
        <Paper p="xl" shadow="sm" radius="md" withBorder>
          <Stack align="center" gap="md">
            <ThemeIcon size={60} radius="xl" color="red">
              <IconAlertCircle size={30} />
            </ThemeIcon>
            <Title order={2}>Invalid Link</Title>
            <Text c="dimmed" ta="center">
              This submission link is invalid or has been deactivated.
              Please contact the project manager for a valid link.
            </Text>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // Offline with no cache - show message
  if (!isOnline && !cachedProject && !displayProjectName) {
    return (
      <Container size="sm" py="xl">
        <Paper p="xl" shadow="sm" radius="md" withBorder>
          <Stack align="center" gap="md">
            <ThemeIcon size={60} radius="xl" color="orange">
              <IconWifiOff size={30} />
            </ThemeIcon>
            <Title order={2}>You're Offline</Title>
            <Text c="dimmed" ta="center">
              This submission link needs to be loaded once while online
              before it can work offline.
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              Please connect to the internet and refresh the page.
            </Text>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // Success state (online submission)
  if (submitMutation.isSuccess) {
    return (
      <Container size="sm" py="xl">
        <Paper p="xl" shadow="sm" radius="md" withBorder>
          <Stack align="center" gap="md">
            <ThemeIcon size={60} radius="xl" color="green">
              <IconCheck size={30} />
            </ThemeIcon>
            <Title order={2}>Submission Received!</Title>
            <Text ta="center">
              Your submission has been sent to the project manager.
            </Text>
            <Paper p="md" bg="gray.0" radius="md" w="100%">
              <Stack gap="xs" align="center">
                <Text size="sm" c="dimmed">
                  Reference Number
                </Text>
                <Text size="xl" fw={700}>
                  {submitMutation.data.referenceNumber}
                </Text>
                <Text size="xs" c="dimmed">
                  Save this number for your records
                </Text>
              </Stack>
            </Paper>
            <Button variant="light" onClick={resetForm}>
              Submit Another
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // Queued state (offline submission)
  if (queuedSubmission) {
    return (
      <Container size="sm" py="xl">
        <Paper p="xl" shadow="sm" radius="md" withBorder>
          <Stack align="center" gap="md">
            <ThemeIcon size={60} radius="xl" color="blue">
              <IconClock size={30} />
            </ThemeIcon>
            <Title order={2}>Submission Queued</Title>
            <Text ta="center">
              Your submission has been saved and will be sent automatically
              when you're back online.
            </Text>
            <Alert color="blue" icon={<IconWifiOff size={16} />} w="100%">
              <Text size="sm">
                You're currently offline. Your submission is safely stored and
                will sync when connectivity is restored.
              </Text>
            </Alert>
            <Button variant="light" onClick={resetForm}>
              Submit Another
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // Form
  return (
    <Box
      style={{
        minHeight: "100vh",
        backgroundColor: "var(--mantine-color-gray-0)",
      }}
    >
      <Container size="sm" py="xl">
        {/* Offline Banner */}
        {!isOnline && (
          <Alert
            color="orange"
            icon={<IconWifiOff size={16} />}
            mb="md"
            radius="md"
          >
            <Group justify="space-between">
              <Text size="sm" fw={500}>
                You're offline - submissions will be queued
              </Text>
              <Badge color="orange" variant="light">
                Offline Mode
              </Badge>
            </Group>
          </Alert>
        )}

        {/* Pending Submissions Banner */}
        {pendingSubmissions.length > 0 && (
          <Alert
            color="blue"
            icon={<IconClock size={16} />}
            mb="md"
            radius="md"
          >
            <Group justify="space-between">
              <Text size="sm">
                {pendingSubmissions.length} submission{pendingSubmissions.length > 1 ? "s" : ""} waiting to sync
              </Text>
              {isOnline && (
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => triggerInboxSync()}
                  loading={syncStatus.isSyncing}
                >
                  Sync Now
                </Button>
              )}
            </Group>
          </Alert>
        )}

        {/* Header */}
        <Paper p="lg" shadow="sm" radius="md" withBorder mb="md">
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Submit to Project
              </Text>
              {isOnline ? (
                <Badge color="green" variant="light" leftSection={<IconWifi size={12} />}>
                  Online
                </Badge>
              ) : (
                <Badge color="orange" variant="light" leftSection={<IconWifiOff size={12} />}>
                  Offline
                </Badge>
              )}
            </Group>
            <Title order={2}>{displayProjectName}</Title>
          </Stack>
        </Paper>

        {/* Form */}
        <Paper p="lg" shadow="sm" radius="md" withBorder>
          <Stack gap="md">
            {submitMutation.isError && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Submission Failed"
                color="red"
              >
                {submitMutation.error?.message || "Please try again"}
              </Alert>
            )}

            <TextInput
              label="Your Name"
              placeholder="Enter your name"
              required
              value={submitterName}
              onChange={(e) => setSubmitterName(e.target.value)}
              size="md"
            />

            <TextInput
              label="Phone (optional)"
              placeholder="For follow-up questions"
              value={submitterPhone}
              onChange={(e) => setSubmitterPhone(e.target.value)}
              size="md"
            />

            <TextInput
              label="Email (optional)"
              placeholder="For confirmation"
              type="email"
              value={submitterEmail}
              onChange={(e) => setSubmitterEmail(e.target.value)}
              size="md"
            />

            <Select
              label="Category"
              data={[
                { value: "checkpoint", label: "Checkpoint / Milestone" },
                { value: "issue", label: "Issue / Problem" },
                { value: "delivery", label: "Delivery" },
                { value: "receipt", label: "Receipt / Invoice" },
                { value: "general", label: "General" },
              ]}
              value={category}
              onChange={setCategory}
              size="md"
            />

            <Textarea
              label="Message"
              placeholder="Describe what you're submitting..."
              required
              minRows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              size="md"
            />

            {/* Photo upload */}
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Photo (optional)
              </Text>
              {photoPreview ? (
                <Box pos="relative">
                  <Image
                    src={photoPreview}
                    alt="Preview"
                    radius="md"
                    mah={200}
                    fit="contain"
                  />
                  <Button
                    size="xs"
                    color="red"
                    variant="filled"
                    pos="absolute"
                    top={8}
                    right={8}
                    onClick={clearPhoto}
                    leftSection={<IconX size={14} />}
                  >
                    Remove
                  </Button>
                </Box>
              ) : (
                <FileButton
                  resetRef={resetRef}
                  onChange={handlePhotoSelect}
                  accept="image/*"
                >
                  {(props) => (
                    <Button
                      variant="light"
                      leftSection={<IconCamera size={18} />}
                      {...props}
                    >
                      Add Photo
                    </Button>
                  )}
                </FileButton>
              )}
            </Stack>

            <Group justify="flex-end" mt="md">
              <Button
                size="lg"
                leftSection={isOnline ? <IconSend size={18} /> : <IconClock size={18} />}
                onClick={handleSubmit}
                loading={submitMutation.isPending}
                disabled={!submitterName.trim() || !message.trim()}
                color={isOnline ? "blue" : "orange"}
              >
                {isOnline ? "Submit" : "Queue for Later"}
              </Button>
            </Group>
          </Stack>
        </Paper>

        {/* Footer */}
        <Text size="xs" c="dimmed" ta="center" mt="lg">
          {isOnline
            ? "This is a secure submission link. Your information will only be visible to the project manager."
            : "Your submission will be securely stored and sent when you're back online."}
        </Text>
      </Container>
    </Box>
  );
}
